import {
  prisma,
  reportingPrisma,
  Prisma,
  MeetingType,
  MeetingParticipantType,
  ParticipantType,
  BookingStatus,
} from "client";
import { transformSurveyResult } from "services/formTemplates";
import {
  normalizeGlobalEventSearchPagination,
  pageGlobalSearchRows,
  selectMeetingsForGlobalSearch,
} from "./global-search.pure";

export {
  GLOBAL_EVENT_SEARCH_DEFAULT_PAGE_SIZE,
  GLOBAL_EVENT_SEARCH_MAX_PAGE_SIZE,
  globalSearchWantsProfiles,
  globalSearchWantsSessions,
  normalizeGlobalEventSearchPagination,
  resolveGlobalSearchWindow,
} from "./global-search.pure";

export const createSessionMeeting = async ({
  eventId,
  createdById,
  startDate,
  translations,
  participantIds,
  endDate,
  typeId,
  trackId,
  topicIds,
  roomId,
  attendeeLimit,
}: {
  eventId: string;
  createdById: string;
  startDate: Date;
  translations: Prisma.MeetingTranslationsCreateManyInput;
  participantIds?: string[];
  endDate?: Date;
  typeId?: string;
  trackId?: string;
  topicIds?: string[];
  roomId?: string;
  attendeeLimit?: number;
}) => {
  if (endDate && endDate <= startDate) {
    throw new Error("End date must be after the start date.");
  }

  if (!participantIds?.length) {
    throw new Error(
      "Session requires at least one speaker linked to the track"
    );
  }

  // Create session meeting without availability check
  const meeting = await prisma.meeting.create({
    data: {
      eventId,
      startDate,
      endDate: endDate || null,
      type: MeetingType.SESSION,
      createdById,
      trackId: trackId || null,
      sessionTypeId: typeId || null,
      topics: topicIds?.length
        ? {
          connect: topicIds.map((id) => ({
            id,
          })),
        }
        : undefined,
      limit: attendeeLimit || null,
      participants: participantIds?.length
        ? {
          createMany: {
            data: participantIds.map((id) => ({
              entityId: id,
              type: MeetingParticipantType.SPEAKER,
              entityType: ParticipantType.USER,
            })),
          },
        }
        : undefined,

      translations: {
        createMany: {
          data: translations,
        },
      },
    },
  });
  if (roomId) {
    await prisma.meetingRoomBooking.create({
      data: {
        date: startDate,
        roomId,
        meetingId: meeting.id,
        createdById,
        status: BookingStatus.BOOKED,
      },
    });
  }

  return meeting;
};
export const updateSessionMeeting = async ({
  meetingId,
  userId,
  startDate,
  endDate,
  translations,
  participantIds,
  typeId,
  trackId,
  topicIds,
  roomId,
  attendeeLimit,
}: {
  meetingId: string;
  userId: string;
  startDate?: Date;
  endDate?: Date;
  translations?: Prisma.MeetingTranslationsCreateManyInput;
  participantIds?: string[];
  typeId?: string;
  trackId?: string;
  topicIds?: string[];
  roomId?: string;
  attendeeLimit?: number;
}) => {
  const existingMeeting = await prisma.meeting.findUnique({
    where: { id: meetingId },
    select: { startDate: true },
  });

  if (!existingMeeting) {
    throw new Error("Meeting not found.");
  }

  // Validate endDate if provided
  if (endDate && startDate) {
    if (endDate <= startDate) {
      throw new Error("End date must be after the start date.");
    }
  } else if (endDate && endDate <= existingMeeting.startDate) {
    throw new Error("End date must be after the existing start date.");
  }

  if (participantIds !== undefined && !participantIds.length) {
    throw new Error(
      "Session requires at least one speaker linked to the track"
    );
  }

  const updateData: any = {};

  if (startDate) updateData.startDate = startDate;
  if (endDate) updateData.endDate = endDate;
  if (typeId) updateData.sessionTypeId = typeId;
  if (trackId) updateData.trackId = trackId;
  if (topicIds)
    updateData.topics = {
      connect: topicIds.map((id) => ({ id })),
    };
  if (attendeeLimit !== undefined) updateData.limit = attendeeLimit;

  if (translations) {
    updateData.translations = {
      deleteMany: {},
      createMany: {
        data: translations,
      },
    };
  }

  if (participantIds?.length) {
    updateData.participants = {
      deleteMany: {},
      createMany: {
        data: participantIds.map((id) => ({
          entityId: id,
          entityType: ParticipantType.USER,
          type: MeetingParticipantType.SPEAKER,
        })),
      },
    };
  }

  // Update the meeting
  const updatedMeeting = await prisma.meeting.update({
    where: { id: meetingId },
    data: updateData,
  });
  if (roomId?.length) {
    await prisma.meetingRoomBooking.deleteMany({
      where: {
        meetingId,
      },
    });
    await prisma.meetingRoomBooking.create({
      data: {
        date: updatedMeeting.startDate,
        roomId,
        meetingId: meetingId,
        createdById: userId,
        status: BookingStatus.BOOKED,
      },
    });
  }
  return updatedMeeting;
};

// export const joinOrLeaveSessionMeeting = async ({
//   userId,
//   meetingId,
// }: {
//   userId: string;
//   meetingId: string;
// }) => {
//   // Check if the meeting exists and is of type SESSION
//   const meeting = await prisma.meeting.findFirst({
//     where: { id: meetingId, type: MeetingType.SESSION },
//     include: { participants: true },
//   });

//   if (!meeting) {
//     throw new Error("Meeting not found.");
//   }

//   // Check if the user is already a participant
//   const isParticipant = meeting?.participantIds?.some(
//     (participantId: string) => participantId === userId
//   );

//   if (isParticipant) {
//     // If the user is already a participant, they want to leave the session
//     await prisma.meeting.update({
//       where: { id: meetingId },
//       data: {
//         participants: {
//           disconnect: { id: userId },
//         },
//       },
//     });

//     return { message: `User ${userId} successfully left the session.` };
//   } else {
//     // If the user is not a participant, they want to join the session
//     await prisma.meeting.update({
//       where: { id: meetingId },
//       data: {
//         participants: {
//           connect: { id: userId },
//         },
//       },
//     });

//     return { message: `User ${userId} successfully joined the session.` };
//   }
// };

export const getSessionMeetings = async ({
  eventId,
  day,
}: {
  eventId: string;
  day?: Date;
}) => {
  try {
    let startOfDayUTC: Date | undefined;
    let endOfDayUTC: Date | undefined;
    if (day) {
      const dateObj = day instanceof Date ? day : new Date(day);

      const startOfDay = new Date(dateObj);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(dateObj);
      endOfDay.setHours(23, 59, 59, 999);
      startOfDayUTC = new Date(startOfDay.toISOString());
      endOfDayUTC = new Date(endOfDay.toISOString());
    }

    const sessionMeetings = await prisma.meeting.findMany({
      where: {
        type: MeetingType.SESSION,
        eventId,
        ...(day && {
          startDate: {
            gte: startOfDayUTC,
            lte: endOfDayUTC,
          },
        }),
      },
      include: {
        participants: {
          include: {
            user: true,
          },
        },
        translations: true,
        sessionType: {
          include: {
            translations: true,
          },
        },
        roomBooking: {
          include: {
            room: {
              include: {
                translations: true,
              },
            },
          },
        },
        track: {
          include: {
            translations: true,
          },
        },
        topics: true,
      },
    });

    return sessionMeetings;
  } catch (error) {
    console.error("Error fetching session meetings:", error);
    throw new Error("Failed to fetch session meetings.");
  }
};

function sessionMeetingListInclude(
  eventId: string,
  lang?: string,
  userId?: string
) {
  return {
    participants: {
      include: {
        user: {
          include: {
            networking: true,
            tags: {
              where: {
                eventId,
              },
            },
            profiles: {
              where: {
                eventId,
              },
              orderBy: {
                createdAt: "desc",
              },
              take: 1,
              include: {
                participantGroups: {
                  select: {
                    translations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    },
                    type: true,
                    color: true,
                  },
                },
              },
            },
            ...(userId
              ? {
                  connectionRequestsReceived: {
                    where: {
                      OR: [
                        {
                          senderId: userId,
                        },
                        {
                          receiverId: userId,
                        },
                      ],
                    },
                    select: {
                      senderId: true,
                      receiverId: true,
                      status: true,
                    },
                  },
                  connectionRequestsSent: {
                    where: {
                      OR: [
                        {
                          senderId: userId,
                        },
                        {
                          receiverId: userId,
                        },
                      ],
                    },
                    select: {
                      senderId: true,
                      receiverId: true,
                      status: true,
                    },
                  },
                  bookmarks: {
                    where: {
                      userId,
                    },
                    select: {
                      entityId: true,
                    },
                  },
                }
              : {}),
          },
        },
      },
    },
    translations: {
      where: {
        language: {
          code: lang,
        },
      },
    },
    sessionType: {
      include: {
        translations: {
          where: {
            language: {
              code: lang,
            },
          },
        },
      },
    },
    roomBooking: {
      include: {
        room: {
          include: {
            translations: {
              where: {
                language: {
                  code: lang,
                },
              },
            },
          },
        },
      },
    },
    topics: true,
    ...(userId
      ? {
          bookmarks: {
            where: {
              userId,
            },
          },
        }
      : {}),
  } satisfies Prisma.MeetingInclude;
}

type SessionMeetingParticipantRow = {
  user: {
    id: string;
    connectionRequestsReceived?: Array<{
      senderId: string;
      receiverId: string;
      status: string;
    }>;
    connectionRequestsSent?: Array<{
      senderId: string;
      receiverId: string;
      status: string;
    }>;
    bookmarks?: Array<{ entityId: string }>;
  } | null;
};

type SessionMeetingListRow = {
  bookmarks?: Array<unknown> | null;
  participants: SessionMeetingParticipantRow[];
};

function presentSessionMeetings<T extends SessionMeetingListRow>(
  sessionMeetings: T[],
  userId?: string
) {
  return sessionMeetings.map((session) => {
    const isBookmarked = !!(session?.bookmarks && session.bookmarks.length > 0);

    const enhancedParticipants = session.participants.map((participant) => {
      const user = participant.user;

      let connectionRequests: string | null = null;
      if (userId && user) {
        const allConnections = [
          ...(user.connectionRequestsReceived || []),
          ...(user.connectionRequestsSent || []),
        ];

        const connection = allConnections.find(
          (request) =>
            (request.senderId === userId && request.receiverId === user.id) ||
            (request.receiverId === userId && request.senderId === user.id)
        );

        connectionRequests = connection?.status ?? null;
      }

      const userBookmarkedThis = participant.user?.bookmarks?.some(
        (bookmark) => bookmark.entityId === participant.user?.id
      );

      return {
        ...participant,
        connectionRequests,
        isBookmarked: userBookmarkedThis || false,
      };
    });

    return {
      ...session,
      isBookmarked,
      participants: enhancedParticipants,
    };
  });
}

function sessionMeetingPageResult<T extends SessionMeetingListRow>(
  rows: T[],
  total: number,
  page: number,
  limit: number,
  userId?: string
) {
  const totalPages = Math.ceil(total / limit);
  return {
    data: presentSessionMeetings(rows, userId),
    userId,
    pagination: {
      total,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
      currentPage: page,
    },
  };
}

export const getSessionMeetingPaginated = async ({
  eventId,
  search,
  hall,
  type,
  track,
  topics,
  day,
  lang,
  userId,
  speakerId,
  page = 1,
  limit = 10,
}: {
  eventId: string;
  search?: string;
  hall?: string[];
  type?: string[];
  track?: string[];
  topics?: string[];
  day?: Date;
  lang?: string;
  userId?: string;
  speakerId?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    let startOfDayUTC: Date | undefined;
    let endOfDayUTC: Date | undefined;

    const whereClause: Prisma.MeetingWhereInput = {
      type: MeetingType.SESSION,
      eventId,
    };

    if (day) {
      const dateObj = day instanceof Date ? day : new Date(day);
      startOfDayUTC = new Date(dateObj.setHours(0, 0, 0, 0));
      endOfDayUTC = new Date(dateObj.setHours(23, 59, 59, 999));

      whereClause.startDate = {
        gte: startOfDayUTC,
        lte: endOfDayUTC,
      };
    }

    if (search) {
      whereClause.translations = {
        some: {
          AND: [
            {
              OR: [
                {
                  name: {
                    contains: search,
                    mode: "insensitive",
                  },
                },
                {
                  description: {
                    contains: search,
                    mode: "insensitive",
                  },
                },
              ],
            },
          ],
        },
      };
    }
    if (hall) {
      whereClause.roomBooking = {
        room: {
          id: {
            in: hall,
          },
        },
      };
    }

    if (type && type.length > 0) {
      whereClause.sessionTypeId = { in: type };
    }

    if (track && track.length > 0) {
      whereClause.trackId = { in: track };
    }

    if (topics && topics.length > 0) {
      whereClause.topicIds = {
        hasSome: topics,
      };
    }

    if (speakerId) {
      whereClause.participants = {
        some: {
          entityId: speakerId,
          type: MeetingParticipantType.SPEAKER,
          entityType: ParticipantType.USER,
        },
      };
    }

    const [sessionMeetings, totalCount] = await Promise.all([
      prisma.meeting.findMany({
        where: whereClause,
        include: sessionMeetingListInclude(eventId, lang, userId),
        skip: (page - 1) * limit,
        take: limit,
        orderBy: {
          startDate: "asc",
        },
      }),
      prisma.meeting.count({
        where: whereClause,
      }),
    ]);

    return sessionMeetingPageResult(
      sessionMeetings,
      totalCount,
      page,
      limit,
      userId
    );
  } catch (error) {
    console.error("Error fetching session meetings:", error);
    throw new Error("Failed to fetch session meetings.");
  }
};

/**
 * Session half of mobile `GET /api/events/[eventId]/search`.
 *
 * Same payload as `getSessionMeetingPaginated` for this call (no hall, track,
 * speaker, or user bookmark filters): `{ data, userId, pagination }`.
 * Reads stay on `reportingPrisma`. A text query does not use translation
 * `contains` (`$regex`); this event's session meetings are read on
 * Meeting(eventId, type), matched in process, and only that page is hydrated.
 * `limit` is capped at `GLOBAL_EVENT_SEARCH_MAX_PAGE_SIZE`.
 */
export const getGlobalSearchSessionMeetings = async ({
  eventId,
  search,
  page = 1,
  limit = 10,
  lang,
}: {
  eventId: string;
  search?: string;
  page?: number;
  limit?: number;
  lang?: string;
}) => {
  try {
    const pagination = normalizeGlobalEventSearchPagination(page, limit);
    const include = sessionMeetingListInclude(eventId, lang);
    const where = {
      eventId,
      type: MeetingType.SESSION,
    };

    if (!search) {
      const [rows, total] = await Promise.all([
        reportingPrisma.meeting.findMany({
          where,
          include,
          skip: (pagination.page - 1) * pagination.limit,
          take: pagination.limit,
          orderBy: { startDate: "asc" },
        }),
        reportingPrisma.meeting.count({ where }),
      ]);
      return sessionMeetingPageResult(
        rows,
        total,
        pagination.page,
        pagination.limit
      );
    }

    const candidates = await reportingPrisma.meeting.findMany({
      where,
      select: {
        id: true,
        startDate: true,
        translations: {
          select: { name: true, description: true },
        },
      },
    });
    const matched = selectMeetingsForGlobalSearch(candidates, search);
    const window = pageGlobalSearchRows(
      matched,
      pagination.page,
      pagination.limit
    );
    const ids = window.pageRows.map((row) => row.id);
    const hydrated = ids.length
      ? await reportingPrisma.meeting.findMany({
          where: {
            id: { in: ids },
            eventId,
            type: MeetingType.SESSION,
          },
          include,
        })
      : [];
    const byId = new Map(hydrated.map((row) => [row.id, row]));
    const ordered = ids.flatMap((id) => {
      const row = byId.get(id);
      return row ? [row] : [];
    });

    return sessionMeetingPageResult(
      ordered,
      window.total,
      window.currentPage,
      pagination.limit
    );
  } catch (error) {
    throw error instanceof Error
      ? error
      : new Error("Failed to fetch session meetings.");
  }
};
export const getSessionMeetingById = async ({
  meetingId,
  eventId,
  lang,
  userId,
}: {
  meetingId: string;
  eventId: string;
  lang?: string;
  userId?: string;
}) => {
  try {
    const whereClause: Prisma.MeetingWhereUniqueInput = {
      id: meetingId,
    };

    const data = await prisma.meeting.findUnique({
      where: whereClause,
      include: {
        formResults: {
          include: {
            formResult: {
              include: {
                formTemplate: true,
              },
            },
          },
        },
        participants: {
          include: {
            user: {
              include: {
                networking: true,
                tags: {
                  where: {
                    eventId,
                  },
                },
                profiles: {
                  where: {
                    eventId,
                  },
                  orderBy: {
                    createdAt: "desc",
                  },
                  take: 1,
                  include: {
                    participantGroups: {
                      select: {
                        translations: {
                          where: {
                            language: {
                              code: lang,
                            },
                          },
                        },
                        type: true,
                        color: true,
                      },
                    },
                  },
                },
                ...(userId && {
                  connectionRequestsReceived: {
                    where: {
                      OR: [{ senderId: userId }, { receiverId: userId }],
                    },
                    select: {
                      senderId: true,
                      receiverId: true,
                      status: true,
                    },
                  },
                  connectionRequestsSent: {
                    where: {
                      OR: [{ senderId: userId }, { receiverId: userId }],
                    },
                    select: {
                      senderId: true,
                      receiverId: true,
                      status: true,
                    },
                  },
                  bookmarks: {
                    where: {
                      userId,
                    },
                    select: {
                      entityId: true,
                    },
                  },
                }),
              },
            },
          },
        },

        translations: {
          where: {
            language: {
              code: lang,
            },
          },
        },
        sessionType: {
          include: {
            translations: {
              where: {
                language: {
                  code: lang,
                },
              },
            },
          },
        },
        track: {
          include: {
            translations: {
              where: {
                language: {
                  code: lang,
                },
              },
            },
          },
        },
        topics: true,
        ...(userId && {
          bookmarks: {
            where: {
              userId,
            },
          },
        }),
      },
    });
    if (!data) return null;

    const enhancedParticipants = data.participants.map((p) => {
      const user = p.user;

      let connectionRequests: string | null = null;
      if (userId && user) {
        const allConnections = [
          ...(user.connectionRequestsReceived || []),
          ...(user.connectionRequestsSent || []),
        ];

        const connection = allConnections.find(
          (cr) =>
            (cr.senderId === userId && cr.receiverId === user.id) ||
            (cr.receiverId === userId && cr.senderId === user.id)
        );

        connectionRequests = connection?.status ?? null;
      }

      const isBookmarkedByUser = user?.bookmarks?.some(
        (b) => b.entityId === user?.id
      );

      return {
        ...p,
        connectionRequests,
        isBookmarked: isBookmarkedByUser || false,
      };
    });

    return {
      ...data,
      formResults: data.formResults.map((fr) => {
        return transformSurveyResult(
          fr.formResult?.formTemplate?.json,
          fr.formResult?.surveyResult
        );
      }),
      participants: enhancedParticipants,
      isBookmarked: !!(data?.bookmarks && data.bookmarks.length > 0),
    };
  } catch (error) {
    console.error("Error fetching session meetings:", error);
    throw new Error("Failed to fetch session meetings.");
  }
};
